import type { PostGridObjectStatus } from '@/services/api/generated/postgridService';

/**
 * All available PostGrid object status values for runtime usage
 */
export const POST_GRID_OBJECT_STATUS_VALUES: PostGridObjectStatus[] = [
  'creating',
  'ready',
  'printing',
  'in_transit',
  'in_local_area',
  'processed_for_delivery',
  'processed',
  'delivered',
  're-routed',
  'returned_to_sender',
  'cancelled',
  'completed',
  'failed',
];

export interface StatusConfig {
  label: string;
  description: string;
  badgeClassName: string;
  bannerClassName: string;
  iconColor: string;
  category: 'success' | 'progress' | 'error' | 'inactive';
}

const getInProgressLabel = (status: PostGridObjectStatus): string => {
  switch (status) {
    case 'creating':
      return 'Creating';
    case 'processed':
      return 'Processing';
    case 'printing':
      return 'Printing';
    case 'in_transit':
      return 'In Transit';
    case 'in_local_area':
      return 'In Local Area';
    case 'processed_for_delivery':
      return 'Processed for Delivery';
    case 'ready':
      return 'Ready';
    default:
      return status;
  }
};

const getInactiveLabel = (status: PostGridObjectStatus): string => {
  switch (status) {
    case 'cancelled':
      return 'Cancelled';
    case 'returned_to_sender':
      return 'Returned to Sender';
    default:
      return status;
  }
};

const getStatusDescription = (status: PostGridObjectStatus): string => {
  switch (status) {
    case 'creating':
      return 'The item is being created in the system';
    case 'ready':
      return 'The item is ready to be processed';
    case 'processed':
      return 'The item is currently being processed';
    case 'printing':
      return 'The item is being printed';
    case 'in_transit':
      return 'The item is in transit to its destination';
    case 'in_local_area':
      return 'The item has arrived in the local delivery area';
    case 'processed_for_delivery':
      return 'The item has been processed and is ready for delivery';
    case 'delivered':
      return 'The item has been successfully delivered';
    case 'completed':
      return 'Mail has been handed off to the postal carrier';
    case 'failed':
      return 'The process has failed';
    case 'cancelled':
      return 'The item has been cancelled';
    case 'returned_to_sender':
      return 'The item has been returned to the sender';
    case 're-routed':
      return 'The item has been re-routed to a different destination';
    default:
      return 'Status information';
  }
};

/**
 * Centralized status configuration for all PostGrid status indicators
 */
export const getStatusConfig = (status: PostGridObjectStatus): StatusConfig => {
  switch (status) {
    // Delivered - confirmed delivery (green)
    case 'delivered':
      return {
        label: 'Delivered',
        description: getStatusDescription(status),
        badgeClassName: 'bg-green-50 text-green-700 border border-green-200',
        bannerClassName: 'bg-green-50 text-green-700 border-l-green-500',
        iconColor: 'text-green-500',
        category: 'success',
      };

    // Completed - handed off to carrier, NOT confirmed delivery (blue/progress)
    case 'completed':
      return {
        label: 'Sent to Carrier',
        description:
          'Mail has been handed off to the postal carrier. Tracking updates may follow for certified mail.',
        badgeClassName: 'bg-blue-50 text-blue-700 border border-blue-200',
        bannerClassName: 'bg-blue-50 text-blue-700 border-l-blue-500',
        iconColor: 'text-blue-500',
        category: 'progress',
      };

    // Error/Failed states - red
    case 'failed':
      return {
        label: 'Failed',
        description: getStatusDescription(status),
        badgeClassName: 'bg-red-50 text-red-700 border border-red-200',
        bannerClassName: 'bg-red-50 text-red-700 border-l-red-500',
        iconColor: 'text-red-500',
        category: 'error',
      };

    // In progress states - blue
    case 'creating':
    case 'processed':
    case 'printing':
    case 'in_transit':
    case 'in_local_area':
    case 'processed_for_delivery':
      return {
        label: getInProgressLabel(status),
        description: getStatusDescription(status),
        badgeClassName: 'bg-blue-50 text-blue-700 border border-blue-200',
        bannerClassName: 'bg-blue-50 text-blue-700 border-l-blue-500',
        iconColor: 'text-blue-500',
        category: 'progress',
      };

    // Inactive/cancelled states - gray
    case 'cancelled':
    case 'returned_to_sender':
    case 're-routed':
    case 'ready':
    default:
      return {
        label: getInactiveLabel(status),
        description: getStatusDescription(status),
        badgeClassName: 'bg-gray-50 text-gray-700 border border-gray-200',
        bannerClassName: 'bg-gray-50 text-gray-700 border-l-gray-500',
        iconColor: 'text-gray-500',
        category: 'inactive',
      };
  }
};

/**
 * Get status icon component based on category
 */
export const getStatusIcon = (category: StatusConfig['category']): string => {
  switch (category) {
    case 'success':
      return 'CheckCircleIcon';
    case 'error':
      return 'XCircleIcon';
    case 'progress':
      return 'ClockIcon';
    case 'inactive':
      return 'MinusCircleIcon';
    default:
      return 'QuestionMarkCircleIcon';
  }
};
